import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { RapoarteService } from './rapoarte.service';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { DocumentTypesService } from 'src/app/platform/nomenclatoare/document-types/document-types.service';
import { EntitatiService } from '../entitati/entitati.service';

@Component({
    selector: 'app-rapoarte',
    templateUrl: './rapoarte.component.html',
    styleUrls: ['./rapoarte.component.scss']
})
export class RapoarteComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    dgaspc_id = environment.interop.dgaspcId;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    serii!: Generic[];
    compartimenteActive!: Generic[];
    usersActive!: Generic[];
    tipuriDocumente!: Generic[];
    destinatari!: any[];

    private destroy$ = new Subject<void>();

    filteredSerii: any[] = [];
    seriiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    filteredTipuriDocumente: any[] = [];
    tipuriDocumenteControl = new FormControl();

    destinatariControl = new FormControl();
    isDestinatariLoading = false;
    showDestinatariMessage = true;
    destinatariLoadComplete = false;

    searchParamsForm!: FormGroup;

    initialFormValues = {
        date_from: '',
        date_to: '',
        date_finalized_from: '',
        date_finalized_to: '',
        registru_id: -1,
        departament_id: -1,
        user_id: -1,
        destinatar_id: -1,
        tip_document_id: -1,
        id_print: -1,
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            date_from: new FormControl(''),
            date_to: new FormControl(''),
            date_finalized_from: new FormControl(''),
            date_finalized_to: new FormControl(''),
            registru_id: new FormControl(-1),
            departament_id: new FormControl(-1),
            user_id: new FormControl(-1),
            destinatar_id: new FormControl(-1),
            tip_document_id: new FormControl(-1),
            id_print: new FormControl(-1),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);
        this.loadUsers();

        this.filteredSerii = this.serii;
        this.seriiControl.setValue(null);

        this.filteredCompartimente = this.compartimenteActive;
        this.compartimenteControl.setValue(null);

        this.usersControl.setValue(null);

        this.filteredTipuriDocumente = this.tipuriDocumente;
        this.tipuriDocumenteControl.setValue(null);

        this.destinatariControl.setValue(null);
        this.destinatari = [];
        this.showDestinatariMessage = true;
        this.destinatariLoadComplete = false;
        this.isDestinatariLoading = false;

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    selectedRowIds: number[] = [];

    rowCount = 0;
    checkboxSelections = false;

    dtLink = environment.interop.basePath + environment.interop.indoc.rapoarte.getRapoarteDT;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private rapoarteService: RapoarteService,
        public permissionsService: PermissionsService,
        private commonService: CommonService,
        private documentTypesService: DocumentTypesService,
        private entitatiService: EntitatiService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.renderFieldsSearch();
        this.loadSerii();
        this.loadCompartimenteActive(this.dgaspc_id);
        this.loadTipuriDocumente();
        this.loadUsers();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderFieldsSearch() {
        this.seriiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSerii = this.serii;
                    return of(this.serii);
                }
                return this.filterSerii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSerii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.tipuriDocumenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTipuriDocumente = this.tipuriDocumente;
                    return of(this.tipuriDocumente);
                }
                return this.filterTipuriDocumente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTipuriDocumente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.destinatariControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(600)
            )
            .subscribe(destinatar => {
                this.fetchDestinatari(destinatar);
                this.showDestinatariMessage = true;
                this.destinatariLoadComplete = false;
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadSerii() {
        this.isFilterLoading = true;
        this.commonService.getSeriiActive()
            .pipe(
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.serii = results;
                    this.filteredSerii = [...this.serii];
                },
                error: (error) => {
                    console.error('Error in get serii: ', error);
                }
            })
    }

    filterSerii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.serii.filter(serie =>
            this.normalizeString(serie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSerieSelectClosed() {
        const selectedValue = this.seriiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSerii.some(serie =>
                serie.id === this.searchParamsForm.get('registru_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.seriiControl.setValue(null);
                this.filteredSerii = this.serii;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isFilterLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isFilterLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.searchParamsForm.get('departament_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.searchParamsForm.controls['user_id'].patchValue(-1);

        if (compartimentId) {
            this.loadUsers();
        }
    }

    loadUsers() {
        this.isFilterLoading = true;

        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.dgaspc_id,
            compartiment_id: this.searchParamsForm.get('departament_id')?.value !== -1 ? this.searchParamsForm.get('departament_id')?.value : null,
        };

        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isFilterLoading = false))
            ).subscribe({
                next: (res) => {
                    this.usersActive = [...res];
                    this.filteredUsers = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.filteredUsers = [];
                },
            });
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.searchParamsForm.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadTipuriDocumente() {
        this.isFilterLoading = true;
        this.documentTypesService.getActiveList()
            .pipe(
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.tipuriDocumente = results;
                    this.filteredTipuriDocumente = [...this.tipuriDocumente];
                },
                error: (error) => {
                    console.error('Error in get tipuri documente: ', error);
                }
            })
    }

    filterTipuriDocumente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tipuriDocumente.filter(tip =>
            this.normalizeString(tip.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTipuriDocumenteSelectClosed() {
        const selectedValue = this.tipuriDocumenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTipuriDocumente.some(tip =>
                tip.id === this.searchParamsForm.get('tip_document_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tipuriDocumenteControl.setValue(null);
                this.filteredTipuriDocumente = this.tipuriDocumente;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    fetchDestinatari(destinatar: string) {
        if (destinatar && destinatar.length >= 2) {
            this.isDestinatariLoading = true;
            let payload = { search: destinatar };
            this.entitatiService.searchEntitatiDT(payload).subscribe({
                next: (response: any) => {
                    this.destinatari = response.data;
                    this.isDestinatariLoading = false;
                    this.destinatariLoadComplete = true;
                    this.showDestinatariMessage = this.destinatari.length === 0;
                },
                error: (err: any) => {
                    this.isDestinatariLoading = false;
                    this.showDestinatariMessage = true;
                    this.destinatariLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.destinatari = [];
            this.showDestinatariMessage = true;
            this.destinatariLoadComplete = false;
        }
    }

    formatDate(date: Date) {
        if (date) {
            let d = new Date(date),
                month = '' + (d.getMonth() + 1),
                day = '' + d.getDate(),
                year = d.getFullYear();

            if (month.length < 2) month = '0' + month;
            if (day.length < 2) day = '0' + day;

            return [year, month, day].join('-');
        } else return '';
    }

    renderDT() {
        const self = this;
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            order: [[6, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                Object.assign(dataTablesParameters, this.searchParamsForm.value);
                dataTablesParameters.date_from = this.formatDate(this.searchParamsForm.get('date_from')?.value);
                dataTablesParameters.date_to = this.formatDate(this.searchParamsForm.get('date_to')?.value);
                dataTablesParameters.date_finalized_from = this.formatDate(this.searchParamsForm.get('date_finalized_from')?.value);
                dataTablesParameters.date_finalized_to = this.formatDate(this.searchParamsForm.get('date_finalized_to')?.value);

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: `<input type="checkbox" class="row-checkbox" style="width: 20px; height: 20px; accent-color: #0044cc;" id="headerCheckbox"/>`,
                    data: 'id_document',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `<input type="checkbox" class="row-checkbox" style="width: 20px; height: 20px; accent-color: #0044cc;" value="${row.id_document}" />`
                    }
                },
                {
                    title: 'ID',
                    data: 'identificationColumn',
                },
                {
                    title: 'Tip Document',
                    data: 'subject',
                },
                {
                    title: 'Origine Document',
                    data: 'documentSource',
                },
                {
                    title: 'Emitent',
                    data: 'entityData',
                },
                {
                    title: 'Status',
                    data: 'status'
                },
                {
                    title: 'Înregistrat',
                    data: 'reg_date',
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).off('click');
                // Handle click event on the CHECKBOXES
                $(row)
                    .find('.row-checkbox')
                    .off('change')
                    .on('change', function () {
                        let idCerere = Number($(this).val());

                        if ($(this).is(':checked')) {
                            self.selectedRowIds.push(idCerere);
                        } else {
                            self.selectedRowIds = self.selectedRowIds.filter(
                                (id) => id !== idCerere
                            );
                        }

                        if (self.rowCount !== self.selectedRowIds.length) {
                            self.checkHeaderCheckBox(false);
                        } else if (self.rowCount === self.selectedRowIds.length) {
                            self.checkHeaderCheckBox(true);
                        }

                        self.checkboxSelections = self.selectedRowIds.length > 0;
                    });

                return row;
            },
            headerCallback: (row: Node, data: any[] | any) => {
                const headerCheckbox = $(row).find('#headerCheckbox');

                // Remove any existing event handlers to avoid duplicating them
                headerCheckbox.off('change click');

                // Handle the change event for selecting/deselecting all checkboxes
                headerCheckbox.on('change', function () {
                    if ($(this).is(':checked')) {
                        self.selectedRowIds.length = 0;
                        data.forEach((item: any) => {
                            self.selectedRowIds.push(item.id_document);
                        });
                        self.checkMultipleCheckboxesByValue(true);
                    } else {
                        self.selectedRowIds.length = 0;
                        self.checkMultipleCheckboxesByValue(false);
                    }
                    self.checkboxSelections = self.selectedRowIds.length > 0;
                });

                // Prevent sorting when clicking the checkbox
                headerCheckbox.on('click', function (e) {
                    e.stopPropagation();
                });

                return row;
            },
        };
    }

    async getTableRowCount() {
        const table = await this.dtElement.dtInstance;
        this.rowCount = table.rows().count();
    }

    checkHeaderCheckBox(checkValue: boolean) {
        const headerCheckbox = document.getElementById(
            'headerCheckbox'
        ) as HTMLInputElement;
        headerCheckbox.checked = checkValue;
    }

    checkMultipleCheckboxesByValue(checkValue: boolean) {
        let checkboxes = document.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"]'
        );
        checkboxes.forEach((checkbox) => {
            checkbox.checked = checkValue;
        });
    }

    checkboxesReset() {
        this.selectedRowIds.length = 0;
        this.checkMultipleCheckboxesByValue(false);
        this.checkHeaderCheckBox(false);
        this.checkboxSelections = false;
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    generate() {
        if (this.selectedRowIds.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Atenție',
                text: 'Trebuie să selectați cel puțin un document pentru a genera raportul!',
                confirmButtonText: 'OK'
            });
            return;
        }

        this.isLoading = true;

        this.rapoarteService.generateReport(this.selectedRowIds)
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (blob) => {
                    // Deschide PDF-ul într-un tab nou
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');

                    Swal.fire({
                        icon: 'success',
                        title: 'Succes',
                        text: 'Raportul a fost generat cu succes!',
                        confirmButtonText: 'OK'
                    });

                    this.checkboxesReset();
                    this.rerenderDT();
                },
                error: (error) => {
                    console.error('Error generating report:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Eroare',
                        text: 'A apărut o eroare la generarea raportului. Vă rugăm încercați din nou.',
                        confirmButtonText: 'OK'
                    });
                }
            });
    }
}
